import { useNotices } from '@/features/notices/useNotices';
import { useApi } from '@/shared/api';
import { ApiBaseError, HttpResponse, RequestParams } from '@/shared/api/generated/http-client';
import { ApiProfileSimpleResponse, ApiScenarioResponse, ScenarioStatus } from '@/shared/api/types';
import { AssetDto, CollectionName, SJDatabase } from '@/shared/lib/sj-orm';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { ScenarioMetaInfoDto } from '@/shared/lib/sj-orm/models/scenario-meta-info.dto';
import { log } from '@/shared/utils/log';

// export const getNameByStatus = (status: string) => {
export const getStatusName = (status: string) => {
  switch (status) {
    case ScenarioStatus.Active:
      return 'Active';

    case ScenarioStatus.InProgress:
      return 'In progress';

    case ScenarioStatus.Done:
      return 'Done';

    case ScenarioStatus.Draft:
      return 'Draft';

    case ScenarioStatus.Canceled:
      return 'Canceled';

    default:
      return status;
  }
};

export const getAssetsDtos = async (scenario: ApiScenarioResponse, sjDb: SJDatabase) => {
  // assets IDs from the real scenario
  const assetsIds = scenario.dtoIds.filter((id) => id.includes('assets-'));
  // get all assets DTOs by assets IDs
  const assetsDtos =
    sjDb.collections.assets?.findMany((asset) => assetsIds.includes(asset.id)) ?? [];

  return assetsDtos;
};

export const createTestAssets = async (
  assetsDtos: AssetDto[],
  sjDb: SJDatabase,
  publicKey: string,
) => {
  // new test asset DTOs
  const newTestAssets: AssetDto[] = assetsDtos.map((asset) => ({
    ...asset,
    id: sjDb.generateId(CollectionName.ASSETS),
    isTest: true,
  }));
  // test assets creation
  const testAssets = await Promise.all(
    newTestAssets
      .map((asset) => sjDb.collections.assets?.create(asset, [publicKey]))
      .filter((it) => it !== undefined),
  );

  // get all test assets DTOs by assets IDs
  const testDtoIds = testAssets.map((asset) => asset.id);

  return { testAssets, testDtoIds };
};

export const createTestScenario = async ({
  scenario,
  sjDb,
  publicKey,
  api,
  testDtoIds,
  findByDatabaseId,
}: {
  scenario: ApiScenarioResponse;
  sjDb: SJDatabase;
  publicKey: string;
  api: ReturnType<typeof useApi>;
  testDtoIds: DtoId[];
  findByDatabaseId: (scenarioDatabaseId: string) => ScenarioMetaInfoDto | undefined;
}) => {
  // check to scenario notification settings
  const valuesArr = Object.values(scenario.notificationSettings ?? {});
  const notificationSettingsExist =
    scenario.notificationSettings && valuesArr.length ? !valuesArr.includes(undefined) : false;

  const testScenarioResponse = await api.scenarioCreate({
    testDtoIds,
    dtoIds: [],
    triggerCondition: scenario.triggerCondition,
    transferCondition: scenario.transferCondition,
    notificationSettings: notificationSettingsExist
      ? {
          daysBeforeTrigger: scenario.notificationSettings!.daysBeforeTrigger,
          daysBetweenNotifications: scenario.notificationSettings!.daysBetweenNotifications,
          notificationTime: scenario.notificationSettings!.notificationTime,
        }
      : undefined,
    triggers: {
      inactivity:
        scenario.triggers?.inactivity?.afterDays !== undefined
          ? { afterDays: scenario.triggers?.inactivity.afterDays }
          : undefined,
      datetime:
        scenario.triggers?.datetime?.scheduledAt !== undefined
          ? { scheduledAt: scenario.triggers.datetime.scheduledAt }
          : undefined,
      checkMe:
        scenario.triggers?.checkMe?.afterDays !== undefined
          ? { afterDays: scenario.triggers.checkMe.afterDays }
          : undefined,
    },
    transfers: scenario.transfers.map((transfer) => ({
      recipientProfileId: transfer.recipientProfileId,
      delayDays: transfer.delayDays,
      isTest: true,
    })),
  });

  // test scenario meta infos creation
  await sjDb.collections.scenarioMetaInfos?.create(
    {
      id: sjDb.generateId(CollectionName.SCENARIO_META_INFO),
      centralDbId: testScenarioResponse.data?.id ?? '',
      name: findByDatabaseId(scenario.id)?.name ?? '',
    },
    [publicKey],
  );

  return testScenarioResponse;
};

export const getMappedAssetsToPublicKeysResponses = async ({
  getBeneficiaries,
  recipientProfileIds,
  testAssets,
  sjDb,
}: {
  getBeneficiaries: (
    params?: RequestParams,
  ) => Promise<HttpResponse<ApiProfileSimpleResponse[], ApiBaseError>>;
  recipientProfileIds: number[];
  testAssets: AssetDto[];
  sjDb: SJDatabase;
}) => {
  const beneficiaries = await getBeneficiaries();
  const beneficiaryPublicKeys: string[] = [];

  // getting beneficiary public keys
  for (const item of beneficiaries.data ?? []) {
    if (beneficiaryPublicKeys.length === recipientProfileIds.length) break;

    if (recipientProfileIds.includes(item.id) && item.publicKey) {
      beneficiaryPublicKeys.push(item.publicKey);
    }
  }

  // mapping assets to beneficiary public keys
  const mappedAssetsToPublicKeysResponses: boolean[] = await Promise.all(
    testAssets.flatMap((_testAsset) =>
      beneficiaryPublicKeys.map(
        (_key) => sjDb.collections.assets?.putPublicKey(_testAsset.id, _key) ?? false,
      ),
    ),
  );

  return mappedAssetsToPublicKeysResponses;
};

export const errorCase = (
  noticeError: string,
  logError: string,
  notice: ReturnType<typeof useNotices>,
) => {
  notice.error(noticeError);
  log.error(logError);

  return false;
};
